import {
  FC, useCallback, useMemo,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useForm, useFormState } from 'react-final-form';
import { updateForm } from 'src/admin/listing-editor/helpers';
import Icon from 'src/common/components/IconDS22';
import SnoozeIndividualListing from 'src/admin/common/components/SnoozeModal/SnoozeIndividualListing';
import SnoozeBulkListings from 'src/admin/common/components/SnoozeModal/SnoozeBulkListings';
import DatesField from 'src/common/components/DatesField';
import ReasonField from 'src/admin/common/components/SnoozeModal/ReasonField';
import { SnoozeFormState, WizardPage } from 'src/admin/common/components/SnoozeModal/types';
import SnoozeFooter from 'src/admin/common/components/SnoozeModal/SnoozeFooter';
import { EditorListing } from 'src/common/types/api/listings';
import Button from 'src/common/components/ButtonDS22';
import SnoozeBody from 'src/admin/common/components/SnoozeModal/SnoozeBody';
import WizardNavigation from 'src/admin/common/components/SnoozeModal/WizardNavigation';
import useWizardFlow from 'src/common/components/WizardFlow/useWizardFlow';
import { CALENDAR_SECTION, LISTING_SELECTOR_SECTION } from 'src/admin/common/components/SnoozeModal/constants';
import styles from './SnoozeSection.module.scss';

type Props = {
  /** Only show this one listing */
  individualListing: EditorListing | null;
  closeModal: () => void;
};

const SnoozeSection: FC<Props> = ({
  individualListing,
  closeModal,
}) => {
  const [t] = useTranslation('admin-listings');
  const { change, submit } = useForm<SnoozeFormState>();
  const { values, submitting } = useFormState<SnoozeFormState>();
  const {
    snooze_start_date: startDate,
    snooze_end_date: endDate,
    snooze_enabled: enabled,
    boats,
  } = values;

  const selectedListingIds = useMemo(
    () => boats ?? [],
    [boats],
  );

  const { next } = useWizardFlow<WizardPage>();

  const saveDisabled = selectedListingIds.length === 0 || (enabled && !(startDate && endDate));

  const handleToggle = useCallback(() => {
    change('snooze_enabled', !enabled);
  }, [enabled, change]);

  const handleClearDates = () => {
    updateForm<SnoozeFormState>({ snooze_start_date: '', snooze_end_date: '' }, change);
  };

  return (
    <>
      <WizardNavigation>
        <Icon id="moon-filled" />
        {t('snooze-listing--modal-title')}
      </WizardNavigation>
      <SnoozeBody>
        <div className={styles.description}>
          {t('snooze-listing--modal-description')}
        </div>
        <div className={styles.snoozeContainer}>
          {individualListing && (
            <SnoozeIndividualListing
              headline={individualListing.headline}
              imageSrc={individualListing.cover_photo_url}
              checked={!!enabled}
              handleToggle={handleToggle}
            />
          )}
          {!individualListing && (
            <SnoozeBulkListings
              totalListings={selectedListingIds.length}
              checked={!!enabled}
              handleToggle={handleToggle}
              handleClick={() => next(LISTING_SELECTOR_SECTION)}
            />
          )}
        </div>
        {enabled && (
          <div className={styles.fieldsContainer}>
            <DatesField
              startDate={startDate}
              endDate={endDate}
              label={t('snooze-listing--dates-label')}
              clearLabel={t('snooze-listing--dates-clear')}
              handleClearDates={handleClearDates}
              handleClick={() => next(CALENDAR_SECTION)}
            />
            <ReasonField />
          </div>
        )}
        <div className={styles.info}>
          <Icon id="info" className={styles.icon} />
          {t('snooze-listing--modal-info')}
        </div>
      </SnoozeBody>

      <SnoozeFooter separator>
        <Button
          variant="tertiary"
          onClick={closeModal}
          disabled={submitting}
        >
          {t('snooze-listing--modal-cancel')}
        </Button>
        <Button
          loading={submitting}
          onClick={submit}
          disabled={saveDisabled}
        >
          {t('snooze-listing--modal-save')}
        </Button>
      </SnoozeFooter>
    </>
  );
};

export default SnoozeSection;
